import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ApiError, api } from '@/api/client'
import type { User } from '@/api/types'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const bootstrapped = ref(false)
  const loading = ref(false)
  const error = ref<string | null>(null)

  const isAuthenticated = computed(() => user.value !== null)

  let bootstrapPromise: Promise<void> | null = null

  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return
    if (bootstrapPromise) return bootstrapPromise
    loading.value = true
    bootstrapPromise = (async () => {
      try {
        user.value = await api.get<User>('/auth/me')
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          user.value = null
        } else {
          // Network or 5xx — keep user null but surface for diagnostics.
          error.value = e instanceof Error ? e.message : String(e)
          user.value = null
        }
      } finally {
        bootstrapped.value = true
        loading.value = false
        bootstrapPromise = null
      }
    })()
    return bootstrapPromise
  }

  async function refresh(): Promise<void> {
    bootstrapped.value = false
    await bootstrap()
  }

  async function logout(): Promise<void> {
    try {
      await api.post('/auth/logout')
    } catch {
      // Cookie may already be invalid; treat as logged out anyway.
    }
    user.value = null
    bootstrapped.value = true
  }

  return {
    user,
    bootstrapped,
    loading,
    error,
    isAuthenticated,
    bootstrap,
    refresh,
    logout,
  }
})
